import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Generate PDF from HTML element
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {Object} data - Letter data (used for filename)
 * @returns {Promise<Blob>} - PDF as Blob
 */
export const generatePDF = async (element, data) => {
    if (!element) {
        throw new Error("Element not found");
    }

    try {
        // Convert HTML to canvas
        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
        });

        // Get canvas image data
        const imgData = canvas.toDataURL("image/jpeg", 0.8);

        // Create PDF
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Calculate image dimensions
        const imgProps = pdf.getImageProperties(imgData);
        const imgWidth = pdfWidth;
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Scale down if content exceeds page height
        const scaleFactor = imgHeight > pdfHeight ? pdfHeight / imgHeight : 1;

        // Add image to PDF
        pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            imgWidth * scaleFactor,
            imgHeight * scaleFactor
        );

        // Convert to Blob
        const pdfBlob = pdf.output("blob");

        return pdfBlob;
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw new Error("Failed to generate PDF");
    }
};

/**
 * Download PDF directly (alternative method)
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {string} filename - Name of the PDF file
 */
export const downloadPDF = async (element, filename = "document.pdf") => {
    if (!element) {
        throw new Error("Element not found");
    }

    try {
        const canvas = await html2canvas(element, {
            scale: 3,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgProps = pdf.getImageProperties(imgData);
        const imgWidth = pdfWidth;
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

        const scaleFactor = imgHeight > pdfHeight ? pdfHeight / imgHeight : 1;

        pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            imgWidth * scaleFactor,
            imgHeight * scaleFactor
        );

        pdf.save(filename);
    } catch (error) {
        console.error("Error downloading PDF:", error);
        throw new Error("Failed to download PDF");
    }
};